// File: frontend/src/components/admin/adminApi.js
// Shared fetch plumbing for the admin dashboard.
//
// Every tab used to repeat the same twelve lines: build headers, fetch, check
// ok, parse, set state, log, set a per-section error string. That is why the
// error handling drifted between sections.
import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import { logger } from '../../utils/logger';

const log = logger.child('admin');

// Admin access is account-based: the signed-in user's bearer token, and the
// account behind it must carry users.is_admin on the server. There is no
// separate admin credential in the browser.
export function adminHeaders(extra = {}) {
  let token = null;
  try { token = localStorage.getItem('token'); } catch { /* private mode */ }
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

export async function adminFetch(url, options = {}) {
  const res = await fetchWithRetry(url, { ...options, headers: adminHeaders(options.headers) });
  if (!res.ok) {
    // Prefer the server's own explanation. Several admin endpoints answer a
    // 4xx with a reason worth showing verbatim — POST /send-daily-email
    // returns 409 with "not sent (no recipients)", which is the whole point of
    // that status: a bare "HTTP 409" would tell an admin nothing.
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || body.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
}

// One section's data, loading flag, error string and a retry callback.
//
// `path` is a function of the current dependencies rather than a string, so a
// section with filters (security events) re-fetches when they change without
// each caller hand-rolling a useCallback chain.
export function useAdminSection(name, buildPath, deps = [], { enabled = true, initial = null } = {}) {
  const [data, setData] = useState(initial);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Guards against a slow response from a previous filter state overwriting a
  // newer one, and against setting state after unmount.
  const generation = useRef(0);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const path = useCallback(buildPath, deps);

  const load = useCallback(async () => {
    if (!enabled) return;
    const mine = ++generation.current;
    setLoading(true);
    const url = path();
    try {
      const json = await adminFetch(url);
      if (!alive.current || mine !== generation.current) return;
      setData(json);
      setError(null);
    } catch (err) {
      if (!alive.current || mine !== generation.current) return;
      log.error(`${name} failed`, err, { method: 'GET', url });
      setError(err.message || `Failed to load ${name}`);
    } finally {
      if (alive.current && mine === generation.current) setLoading(false);
    }
  }, [enabled, name, path]);

  useEffect(() => { load(); }, [load]);

  return { data, error, loading, reload: load };
}
